import React, { memo, useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Annotation
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import styles from "./MapChart.module.css";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const BEST_BUY_LOGO = "/images/pics/Best_Buy_Logo.svg.png";
const MICRO_CENTER_LOGO = "/images/pics/microcenter-logo.jpeg";

// States too small to hold a logo at geoAlbersUsa scale; their markers are
// pulled out with a connector line instead (dx/dy in px from the centroid).
const SMALL_STATE_OFFSETS = {
  VT: [62, -20],
  NH: [56, -4],
  MA: [58, 8],
  RI: [56, 22],
  CT: [52, 34],
  NJ: [56, 12],
  DE: [52, 4],
  MD: [62, 26]
};

// SVG-unit size of one logo. The 800x600 viewBox typically renders around
// 500px wide, so on-screen size is roughly 60% of this value.
const LOGO_SIZE = 28;
const LOGO_GAP = 4;

const withStoreId = (url, storeId) => {
  if (!url || !storeId) {
    return url;
  }
  return `${url}${url.includes("?") ? "&" : "?"}storeid=${encodeURIComponent(storeId)}`;
};

const RETAILERS = [
  { key: "bestBuy", flag: "hasBestBuy", name: "Best Buy", logo: BEST_BUY_LOGO },
  { key: "microCenter", flag: "hasMicroCenter", name: "Micro Center", logo: MICRO_CENTER_LOGO }
];

const MapChart = ({ stateData, availabilityLabel = "Best Buy or Micro Center", retailers = null }) => {
  const [selectedState, setSelectedState] = useState(null);
  const [copiedZip, setCopiedZip] = useState(null);

  const copyZip = (zip) => {
    const markCopied = () => {
      setCopiedZip(zip);
      setTimeout(() => setCopiedZip((current) => (current === zip ? null : current)), 3000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(zip).then(markCopied).catch(() => {});
      return;
    }
    // navigator.clipboard needs a secure context (absent on http dev hosts);
    // fall back to the legacy selection-based copy.
    const scratch = document.createElement("textarea");
    scratch.value = zip;
    scratch.setAttribute("readonly", "");
    scratch.style.position = "fixed";
    scratch.style.opacity = "0";
    document.body.appendChild(scratch);
    scratch.select();
    try {
      if (document.execCommand("copy")) {
        markCopied();
      }
    } catch (err) { /* leave the chip label unchanged */ }
    document.body.removeChild(scratch);
  };
  const safeStateData = useMemo(
    () => (Array.isArray(stateData) ? stateData : []),
    [stateData]
  );

  const stateInfoByName = useMemo(() => {
    const byName = new Map();
    safeStateData.forEach((state) => {
      if (state && state.stateName) {
        byName.set(state.stateName, state);
      }
    });
    return byName;
  }, [safeStateData]);

  if (safeStateData.length === 0) {
      return <div className={styles.infoContainer}>No state availability data available for this product</div>;
  }

  const availableStates = safeStateData.filter(state => state && state.available).length;

  const retailersForState = (stateInfo) =>
    RETAILERS.filter((retailer) => stateInfo && stateInfo[retailer.flag]);

  const handleStateClick = (stateInfo) => {
    if (!stateInfo || retailersForState(stateInfo).length === 0) {
      return;
    }
    setSelectedState((current) =>
      current && current.stateName === stateInfo.stateName ? null : stateInfo
    );
  };

  const renderLogos = (stateInfo) => {
    const present = retailersForState(stateInfo);
    if (present.length === 0) {
      return null;
    }
    const size = LOGO_SIZE;
    const gap = LOGO_GAP;
    const totalWidth = present.length * size + (present.length - 1) * gap;
    return (
      <g
        className={styles.retailerMarker}
        onClick={(event) => {
          event.stopPropagation();
          handleStateClick(stateInfo);
        }}
      >
        {/* invisible hit area so small logos are still easy to click */}
        <rect
          x={-totalWidth / 2 - 4}
          y={-size / 2 - 4}
          width={totalWidth + 8}
          height={size + 8}
          fill="transparent"
        />
        {present.map((retailer, index) => (
          <image
            key={retailer.key}
            href={retailer.logo}
            x={-totalWidth / 2 + index * (size + gap)}
            y={-size / 2}
            width={size}
            height={size}
            preserveAspectRatio="xMidYMid meet"
          >
            <title>{`${retailer.name} has in-store stock`}</title>
          </image>
        ))}
      </g>
    );
  };

  const selectedRetailers = selectedState ? retailersForState(selectedState) : [];

  return (
    <div className={styles.mapWrapper}>
      <div className={styles.mapContainer}>
        {availableStates === 0 && (
          <div className={styles.noAvailabilityMessage}>
            Not currently available from {availabilityLabel} across the US
          </div>
        )}

        <ComposableMap
            projection="geoAlbersUsa"
            projectionConfig={{
                scale: 1000,
                center: [0, 0]
            }}
            style={{
                width: "100%",
                height: "100%"
            }}
            >
            <Geographies geography={geoUrl}>
            {({ geographies }) => (
                <>
                {geographies.map(geo => {
                const stateName = geo.properties.name;

                const stateInfo = stateInfoByName.get(stateName);

                const available = stateInfo ? stateInfo.available : false;
                const clickable = stateInfo && retailersForState(stateInfo).length > 0;

                return (
                    <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    strokeWidth={0.5}
                    onClick={() => handleStateClick(stateInfo)}
                    style={{
                        /* SVG fill/stroke attributes can't resolve var(); use inline styles */
                        default: {
                        fill: available ? "var(--sm-map-available)" : "var(--sm-map-unavailable)",
                        stroke: "var(--sm-map-stroke)",
                        outline: "none"
                        },
                        hover: {
                        fill: available ? "var(--sm-map-available-hover)" : "var(--sm-map-unavailable-hover)",
                        stroke: "var(--sm-map-stroke)",
                        outline: "none",
                        cursor: clickable ? "pointer" : "default"
                        },
                        pressed: {
                        fill: available ? "var(--sm-map-available)" : "var(--sm-map-unavailable)",
                        stroke: "var(--sm-map-stroke)",
                        outline: "none"
                        }
                    }}
                    title={`${stateName}: ${available ? "Available" : "Not Available"}`}
                    />
                );
                })}
                {geographies.map(geo => {
                const stateInfo = stateInfoByName.get(geo.properties.name);
                if (!stateInfo || retailersForState(stateInfo).length === 0) {
                    return null;
                }
                const centroid = geoCentroid(geo);
                const offset = stateInfo.stateCode
                    ? SMALL_STATE_OFFSETS[stateInfo.stateCode]
                    : null;
                if (offset) {
                    return (
                        <Annotation
                        key={`logo-${geo.rsmKey}`}
                        subject={centroid}
                        dx={offset[0]}
                        dy={offset[1]}
                        connectorProps={{
                            stroke: "var(--sm-map-stroke)",
                            strokeWidth: 0.75
                        }}
                        >
                        {renderLogos(stateInfo)}
                        </Annotation>
                    );
                }
                return (
                    <Marker key={`logo-${geo.rsmKey}`} coordinates={centroid}>
                    {renderLogos(stateInfo)}
                    </Marker>
                );
                })}
                </>
            )}
            </Geographies>
        </ComposableMap>

        {selectedState && selectedRetailers.length > 0 && (
          <div className={styles.retailerPopover} role="dialog" aria-label={`In-store availability in ${selectedState.stateName}`}>
            <div className={styles.popoverHeader}>
              <span className={styles.popoverTitle}>{selectedState.stateName}</span>
              <button
                type="button"
                className={styles.popoverClose}
                aria-label="Close"
                onClick={() => setSelectedState(null)}
              >
                ×
              </button>
            </div>
            {selectedRetailers.map((retailer) => {
              const info = retailers ? retailers[retailer.key] : null;
              let checkoutUrl = info && info.enabled ? info.checkoutUrl : null;
              let locationHint = null;
              if (retailer.key === "microCenter" && selectedState.mcStoreId) {
                // Micro Center honors ?storeid= on product pages, so the link
                // lands with this state's store already selected.
                checkoutUrl = withStoreId(checkoutUrl, selectedState.mcStoreId);
                locationHint = selectedState.mcStoreName || null;
              }
              return (
                <div key={retailer.key} className={styles.popoverRow}>
                  <img src={retailer.logo} alt={retailer.name} className={styles.popoverLogo} />
                  <div className={styles.popoverRetailer}>
                    <span className={styles.popoverRetailerName}>{retailer.name}</span>
                    {info && info.displayPrice && (
                      <span className={styles.popoverPrice}>{info.displayPrice}</span>
                    )}
                    {locationHint && (
                      <span className={styles.popoverLocation}>{locationHint}</span>
                    )}
                    {retailer.key === "bestBuy" && selectedState.zipCode && (
                      // Best Buy has no location deep link - its PDP asks for
                      // a ZIP in the pickup picker, so hand the user the ZIP
                      // of the store with stock (the state's ZIP when no
                      // store is recorded) ready to paste.
                      <button
                        type="button"
                        className={styles.popoverZipCopy}
                        title="Copy this ZIP, then paste it in Best Buy's pickup location picker"
                        onClick={() => copyZip(selectedState.zipCode)}
                      >
                        {copiedZip === selectedState.zipCode
                          ? "Copied ✓ — paste in Best Buy's store picker"
                          : `ZIP ${selectedState.zipCode} · copy`}
                      </button>
                    )}
                  </div>
                  {checkoutUrl ? (
                    <a
                      className={styles.popoverCheckout}
                      href={checkoutUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => {
                        if (retailers && typeof retailers.onCheckout === "function") {
                          retailers.onCheckout(event, checkoutUrl, retailer.name);
                        }
                      }}
                    >
                      Checkout
                    </a>
                  ) : (
                    <span className={styles.popoverInStoreOnly}>In-store</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: "var(--sm-map-available)" }}></div>
          <span>Available</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendColor} style={{ backgroundColor: "var(--sm-map-unavailable)" }}></div>
          <span>Unavailable</span>
        </div>
      </div>

      <div className={styles.statsSection}>
        Available in {availableStates} {availableStates === 1 ? 'state' : 'states'}
      </div>
    </div>
  );
};

export default memo(MapChart);
